(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    define([], function(){ return factory(root); });
  } else if (typeof module === 'object' && module.exports) {
    module.exports = factory(globalThis || (typeof self !== 'undefined' ? self : this));
  } else {
    root.SwishPickleballLines = factory(root);
  }
}(typeof self !== 'undefined' ? self : this, function (GLOBAL) {
  'use strict';

  function normHex(x){ if(!x) return '#FFFFFF'; x=String(x).trim(); if(x[0] !== '#') x='#'+x; return x.toUpperCase(); }
  function hasKeys(obj) {
    if (!obj) return false;
    for (const _ in obj) return true;
    return false;
  }
  function resolveOfficial(rulesetId) {
    const cfg = GLOBAL && GLOBAL.SwishCBConfig;
    if (cfg && typeof cfg.resolveOfficialCourtGeometry === 'function') {
      try { return cfg.resolveOfficialCourtGeometry(rulesetId, { noSeries: true }); } catch (_) {}
    }
    return null;
  }

  const FT = 0.3048;
  const OFFICIAL = resolveOfficial('PICKLEBALL');
  const OFFICIAL_RAW = (OFFICIAL && OFFICIAL.official) || {};
  const COURT = {
    LENGTH: Number(OFFICIAL && OFFICIAL.officialLengthM) || (44 * FT),
    WIDTH: Number(OFFICIAL && OFFICIAL.officialWidthM) || (20 * FT),
    NVZ: Number(OFFICIAL_RAW.nonVolleyZone) || (7 * FT),
    LINE: Number(OFFICIAL && OFFICIAL.lineWidthM) || 0.0508
  };
  const HALF_LENGTH = COURT.LENGTH / 2;
  const HALF_WIDTH = COURT.WIDTH / 2;
  // USA Pickleball measures the NVZ distance to the outside edge of the line.
  const LEFT_SERVICE_END = HALF_LENGTH - COURT.NVZ + (COURT.LINE * 0.5);
  const RIGHT_SERVICE_START = HALF_LENGTH + COURT.NVZ - (COURT.LINE * 0.5);
  const RIGHT_SERVICE_WIDTH = COURT.LENGTH - RIGHT_SERVICE_START;
  const SKIP_COLOR = '__SKIP__';

  function makePart(id, order, role, fallback, side, draw) {
    return { id, order, role, fallback, side, draw };
  }

  function snapAreaRectToTileGrid(dims, x, y, w, h) {
    const tileM = Math.max(0, Number(dims && dims.tileSizeM) || 0);
    if (!(tileM > 0) || String(dims && dims.lineFitMode || '') !== 'clean') return { x, y, w, h };
    const offsetX = Number(dims && dims.tileGridOffsetXM) || 0;
    const offsetY = Number(dims && dims.tileGridOffsetYM) || 0;
    const epsilon = 1e-9;
    const snapStart = (value, offset) => (Math.floor(((offset + value) / tileM) + epsilon) * tileM) - offset;
    const snapEnd = (value, offset) => (Math.ceil(((offset + value) / tileM) - epsilon) * tileM) - offset;
    const courtLengthM = Math.max(0.001, Number(dims && dims.courtLengthM) || COURT.LENGTH);
    const courtWidthM = Math.max(0.001, Number(dims && dims.courtWidthM) || COURT.WIDTH);
    const x0 = Math.max(0, Math.min(courtLengthM, snapStart(x, offsetX)));
    const y0 = Math.max(0, Math.min(courtWidthM, snapStart(y, offsetY)));
    const x1 = Math.max(x0, Math.min(courtLengthM, snapEnd(x + w, offsetX)));
    const y1 = Math.max(y0, Math.min(courtWidthM, snapEnd(y + h, offsetY)));
    return { x: x0, y: y0, w: Math.max(0, x1 - x0), h: Math.max(0, y1 - y0) };
  }

  function resolveMatchedAxis(dims, axis, surfaceM, officialSpanM) {
    const tileM = Math.max(0, Number(dims && dims.tileSizeM) || 0);
    if (!(tileM > 0)) return null;
    const offsetM = axis === 'x'
      ? (Number(dims && dims.tileGridOffsetXM) || 0)
      : (Number(dims && dims.tileGridOffsetYM) || 0);
    const maxHalfTiles = Math.max(1, Math.floor((Math.max(0.001, surfaceM) + 1e-9) / (tileM * 2)));
    const halfTiles = Math.max(1, Math.min(maxHalfTiles, Math.round((officialSpanM * 0.5) / tileM)));
    const halfSpanM = halfTiles * tileM;
    const idealCenterM = surfaceM * 0.5;
    const minIndex = Math.ceil(((halfSpanM + offsetM) / tileM) - 1e-9);
    const maxIndex = Math.floor((((surfaceM - halfSpanM) + offsetM) / tileM) + 1e-9);
    let centerIndex = Math.round((idealCenterM + offsetM) / tileM);
    if (maxIndex >= minIndex) centerIndex = Math.max(minIndex, Math.min(maxIndex, centerIndex));
    const centerM = (centerIndex * tileM) - offsetM;
    return {
      startM: Math.max(0, centerM - halfSpanM),
      endM: Math.min(surfaceM, centerM + halfSpanM),
      centerM,
      halfTiles,
      tileM
    };
  }

  function resolveCourtGeometry(dims) {
    const lineFitMode = String(dims && dims.lineFitMode || '');
    const tiledFit = (lineFitMode === 'clean' || lineFitMode === 'match') && Number(dims && dims.tileSizeM) > 0;
    const surfaceLength = tiledFit
      ? Math.max(0.001, Number(dims && dims.courtLengthM) || COURT.LENGTH)
      : COURT.LENGTH;
    const surfaceWidth = tiledFit
      ? Math.max(0.001, Number(dims && dims.courtWidthM) || COURT.WIDTH)
      : COURT.WIDTH;

    if (lineFitMode === 'match' && Number(dims && dims.tileSizeM) > 0) {
      const matchedLength = resolveMatchedAxis(dims, 'x', surfaceLength, COURT.LENGTH);
      const matchedWidth = resolveMatchedAxis(dims, 'y', surfaceWidth, COURT.WIDTH);
      if (matchedLength && matchedWidth) {
        const tileM = matchedLength.tileM;
        const nvzLineDistanceM = Math.max(tileM, Math.round((COURT.NVZ - (COURT.LINE * 0.5)) / tileM) * tileM);
        const maxNvzDistanceM = Math.max(tileM, (matchedLength.endM - matchedLength.startM) * 0.5 - tileM);
        const matchedNvzDistanceM = Math.min(maxNvzDistanceM, nvzLineDistanceM);
        return {
          x: matchedLength.startM,
          y: matchedWidth.startM,
          right: matchedLength.endM,
          bottom: matchedWidth.endM,
          length: matchedLength.endM - matchedLength.startM,
          width: matchedWidth.endM - matchedWidth.startM,
          height: matchedWidth.endM - matchedWidth.startM,
          surfaceLength,
          surfaceWidth,
          lineScale: 1,
          lineWidth: COURT.LINE,
          halfLength: matchedLength.centerM,
          halfWidth: matchedWidth.centerM,
          leftServiceEnd: matchedLength.centerM - matchedNvzDistanceM,
          rightServiceStart: matchedLength.centerM + matchedNvzDistanceM,
          matchedToTileGrid: true,
          tileM
        };
      }
    }

    // Clean mirrors basketball Clean Key Fit: official sport lines never move.
    // Only the colored zones are expanded by snapAreaRectToTileGrid(). If a
    // customer creates a surface smaller than the official court, preserve the
    // official aspect ratio and scale the complete line system down to fit.
    const lineScale = Math.min(1, surfaceLength / COURT.LENGTH, surfaceWidth / COURT.WIDTH);
    const length = COURT.LENGTH * lineScale;
    const width = COURT.WIDTH * lineScale;
    const x = Math.max(0, (surfaceLength - length) * 0.5);
    const y = Math.max(0, (surfaceWidth - width) * 0.5);
    const right = x + length;
    const bottom = y + width;
    const halfLength = x + (HALF_LENGTH * lineScale);
    const halfWidth = y + (HALF_WIDTH * lineScale);
    const leftServiceEnd = x + (LEFT_SERVICE_END * lineScale);
    const rightServiceStart = x + (RIGHT_SERVICE_START * lineScale);
    return {
      x,
      y,
      right,
      bottom,
      length,
      width,
      height: width,
      surfaceLength,
      surfaceWidth,
      lineScale,
      lineWidth: COURT.LINE * lineScale,
      halfLength,
      halfWidth,
      leftServiceEnd,
      rightServiceStart
    };
  }

  function fillRect(ctx, dims, color, x, y, w, h) {
    const rect = snapAreaRectToTileGrid(dims, x, y, w, h);
    ctx.fillStyle = (GLOBAL.SwishCBHardwood && typeof GLOBAL.SwishCBHardwood.getFillStyle === 'function')
      ? GLOBAL.SwishCBHardwood.getFillStyle(ctx, color, { hardwood: dims.hardwood, pattern: dims.pattern, patternTarget: dims.patternTarget, pixelsPerMeter: dims.scaleAvg })
      : color;
    ctx.fillRect(
      rect.x * dims.scaleX,
      rect.y * dims.scaleY,
      rect.w * dims.scaleX,
      rect.h * dims.scaleY
    );
  }

  // Use filled rects instead of strokes to keep line thickness visually uniform.
  function fillLineH(ctx, dims, color, x1, x2, yM, widthM, align) {
    const wPx = dims.fixedLineWidthPx || (widthM * dims.scaleAvg);
    const xPx1 = x1 * dims.scaleX;
    const xPx2 = x2 * dims.scaleX;
    const left = Math.min(xPx1, xPx2);
    const width = Math.abs(xPx2 - xPx1);
    const yPx = yM * dims.scaleY;
    const top =
      align === 'inside-top' ? yPx :
      align === 'inside-bottom' ? (yPx - wPx) :
      (yPx - wPx / 2);
    ctx.fillStyle = color;
    ctx.fillRect(left, top, width, wPx);
  }

  function fillLineV(ctx, dims, color, xM, y1, y2, widthM, align) {
    const wPx = dims.fixedLineWidthPx || (widthM * dims.scaleAvg);
    const yPx1 = y1 * dims.scaleY;
    const yPx2 = y2 * dims.scaleY;
    const top = Math.min(yPx1, yPx2);
    const height = Math.abs(yPx2 - yPx1);
    const xPx = xM * dims.scaleX;
    const left =
      align === 'inside-left' ? xPx :
      align === 'inside-right' ? (xPx - wPx) :
      (xPx - wPx / 2);
    ctx.fillStyle = color;
    ctx.fillRect(left, top, wPx, height);
  }

  const PARTS = [
    // Left service side
    makePart('pickleLeftUpperService', 10, 'area', 'main', 'left', (ctx, dims, color) => {
      const court = dims.court;
      fillRect(ctx, dims, color, court.x, court.y, court.leftServiceEnd - court.x, court.halfWidth - court.y);
    }),
    makePart('pickleLeftLowerService', 20, 'area', 'main', 'left', (ctx, dims, color) => {
      const court = dims.court;
      fillRect(ctx, dims, color, court.x, court.halfWidth, court.leftServiceEnd - court.x, court.bottom - court.halfWidth);
    }),
    makePart('pickleLeftKitchen', 30, 'area', 'key', 'left', (ctx, dims, color) => {
      const court = dims.court;
      fillRect(ctx, dims, color, court.leftServiceEnd, court.y, court.halfLength - court.leftServiceEnd, court.height);
    }),
    // Right service side
    makePart('pickleRightUpperService', 40, 'area', 'main', 'right', (ctx, dims, color) => {
      const court = dims.court;
      fillRect(ctx, dims, color, court.rightServiceStart, court.y, court.right - court.rightServiceStart, court.halfWidth - court.y);
    }),
    makePart('pickleRightLowerService', 50, 'area', 'main', 'right', (ctx, dims, color) => {
      const court = dims.court;
      fillRect(ctx, dims, color, court.rightServiceStart, court.halfWidth, court.right - court.rightServiceStart, court.bottom - court.halfWidth);
    }),
    makePart('pickleRightKitchen', 60, 'area', 'key', 'right', (ctx, dims, color) => {
      const court = dims.court;
      fillRect(ctx, dims, color, court.halfLength, court.y, court.rightServiceStart - court.halfLength, court.height);
    }),
    // Lines
    makePart('pickleSidelineTop', 200, 'line', 'lines', 'center', (ctx, dims, color) => {
      const court = dims.court;
      fillLineH(ctx, dims, color, court.x, court.right, court.y, court.lineWidth, 'inside-top');
    }),
    makePart('pickleSidelineBottom', 210, 'line', 'lines', 'center', (ctx, dims, color) => {
      const court = dims.court;
      fillLineH(ctx, dims, color, court.x, court.right, court.bottom, court.lineWidth, 'inside-bottom');
    }),
    makePart('pickleBaselineLeft', 220, 'line', 'lines', 'center', (ctx, dims, color) => {
      const court = dims.court;
      fillLineV(ctx, dims, color, court.x, court.y, court.bottom, court.lineWidth, 'inside-left');
    }),
    makePart('pickleBaselineRight', 230, 'line', 'lines', 'center', (ctx, dims, color) => {
      const court = dims.court;
      fillLineV(ctx, dims, color, court.right, court.y, court.bottom, court.lineWidth, 'inside-right');
    }),
    makePart('pickleCenterLine', 240, 'line', 'lines', 'center', (ctx, dims, color) => {
      // Service centerline should stop at each non-volley zone line
      const court = dims.court;
      fillLineH(ctx, dims, color, court.x, court.leftServiceEnd, court.halfWidth, court.lineWidth, 'center');
      fillLineH(ctx, dims, color, court.rightServiceStart, court.right, court.halfWidth, court.lineWidth, 'center');
    }),
    makePart('pickleNvzLineLeft', 250, 'line', 'lines', 'center', (ctx, dims, color) => {
      const court = dims.court;
      fillLineV(ctx, dims, color, court.leftServiceEnd, court.y, court.bottom, court.lineWidth, 'center');
    }),
    makePart('pickleNvzLineRight', 260, 'line', 'lines', 'center', (ctx, dims, color) => {
      const court = dims.court;
      fillLineV(ctx, dims, color, court.rightServiceStart, court.y, court.bottom, court.lineWidth, 'center');
    })
  ];

  const PART_LOOKUP = PARTS.reduce((acc, part) => {
    acc[part.id] = part;
    return acc;
  }, Object.create(null));
  const DRAW_ORDER = PARTS.slice().sort((a, b) => a.order - b.order).map(part => part.id);
  const LINE_ONLY_ORDER = DRAW_ORDER.filter(id => PART_LOOKUP[id] && PART_LOOKUP[id].role === 'line');

  function computeDims(cw, ch, opts) {
    const lineFitMode = String(opts && opts.lineFitMode || '');
    const tiledFit = (lineFitMode === 'clean' || lineFitMode === 'match') && Number(opts && opts.tileSizeM) > 0;
    const courtLengthM = tiledFit
      ? Math.max(0.001, Number(opts && opts.playableLengthM) || COURT.LENGTH)
      : COURT.LENGTH;
    const courtWidthM = tiledFit
      ? Math.max(0.001, Number(opts && opts.playableWidthM) || COURT.WIDTH)
      : COURT.WIDTH;
    const scaleX = cw / courtLengthM;
    const scaleY = ch / courtWidthM;
    return {
      scaleX,
      scaleY,
      scaleAvg: (scaleX + scaleY) * 0.5,
      fixedLineWidthPx: Math.max(0, Number(opts && opts.fixedLineWidthPx) || 0),
      courtLengthM,
      courtWidthM,
      cw,
      ch
    };
  }

  function pickDetailColor(detailColors, fallbackColors, id, fallback, baseColor) {
    if (detailColors && detailColors[id]) {
      if (detailColors[id] === SKIP_COLOR) return null;
      return detailColors[id];
    }
    if (fallbackColors && fallbackColors[id]) {
      if (fallbackColors[id] === SKIP_COLOR) return null;
      return fallbackColors[id];
    }
    if (fallback === 'key') return baseColor.key || baseColor.main;
    if (fallback === 'border') return baseColor.border || baseColor.main;
    if (fallback === 'lines') return baseColor.lines;
    return baseColor.main;
  }

  const DEFAULT_PALETTE = {
    main: '#0F4C81',
    key: '#27C498',
    border: '#0A2F4C',
    lines: '#FFFFFF'
  };

  function draw(ctx, x0, y0, cw, ch, color, opts) {
    if (!ctx || !cw || !ch) return;
    const detailColors = (opts && opts.detailColors) || null;
    const fallbackColors = (opts && opts.fallbackColors) || null;
    const only = (opts && (opts.only === 'left' || opts.only === 'right')) ? opts.only : null;
    const useDetailedParts = !!(opts && opts.useDetailedParts);
    const hasCustom = hasKeys(detailColors) || hasKeys(fallbackColors);
    const drawDetailed = useDetailedParts || hasCustom;
    const order = drawDetailed ? DRAW_ORDER : LINE_ONLY_ORDER;

    ctx.save();
    ctx.translate(x0, y0);
    const dims = computeDims(cw, ch, opts);
    dims.hardwood = opts && opts.hardwood;
    dims.pattern = opts && opts.pattern;
    dims.patternTargetPrefix = opts && opts.patternTargetPrefix;
    dims.lineFitMode = opts && opts.lineFitMode;
    dims.tileSizeM = opts && opts.tileSizeM;
    dims.tileGridOffsetXM = opts && opts.tileGridOffsetXM;
    dims.tileGridOffsetYM = opts && opts.tileGridOffsetYM;
    dims.court = resolveCourtGeometry(dims);
    const palette = {
      main: DEFAULT_PALETTE.main,
      key: DEFAULT_PALETTE.key,
      border: DEFAULT_PALETTE.border,
      lines: normHex(color || DEFAULT_PALETTE.lines)
    };

    for (const id of order) {
      const part = PART_LOOKUP[id];
      if (!part) continue;
      if (only && part.side && part.side !== only && part.side !== 'center') continue;
      const fill = pickDetailColor(detailColors, fallbackColors, id, part.fallback, palette);
      if (!fill) continue;
      try {
        dims.patternTarget = { id: (dims.patternTargetPrefix || '') + id, role: part.role, fallback: part.fallback };
        part.draw(ctx, dims, normHex(fill));
      } catch (err) {
        console.warn('[SwishPickleballLines] draw error for part', id, err);
      }
    }

    ctx.restore();
  }

  const api = {
    design: { W: COURT.WIDTH * 1000, L: COURT.LENGTH * 1000 },
    draw,
    ready: Promise.resolve()
  };

  return api;
}));
